import { Dispatch, MutableRefObject, SetStateAction, memo } from "react"
import { Box, Checkbox, Chip, createTheme, IconButton, ThemeProvider, Tooltip, useTheme } from "@mui/material"
import { DataGrid, GridAutosizeOptions, gridClasses, GridColumnGroupingModel } from "@mui/x-data-grid"
import { GridApiCommunity } from "@mui/x-data-grid/models/api/gridApiCommunity"
import EditIcon from "@mui/icons-material/Edit"
import InfoIcon from '@mui/icons-material/Info';

import { TeamMember } from "../../../types"
import { OpenTeamMemberModal, TeamRoles } from "./types"
import { autoSizeGrid, getFullName, getGroups, getLastLogin, getPhoneNumber, getRoleName } from "./utils/helpers"
import { sendValidationEmail } from "../../../services/teamMemberService"
import { addError, addSuccess } from "../../../redux/alerts"
import { useAppDispatch } from "../../../redux/store"

type TeamMembersTable = {
  apiRef: MutableRefObject<GridApiCommunity>,
  teamMembers: TeamMember[],
  autosizeOptions: GridAutosizeOptions,
  isLoading: boolean,
  setOpenModal: Dispatch<SetStateAction<OpenTeamMemberModal>>,
  pending?: boolean,
}

const TeamMembersTable = (props: TeamMembersTable) => {
  const {
    apiRef,
    teamMembers,
    autosizeOptions,
    isLoading,
    setOpenModal,
    pending,
  } = props

  const theme = useTheme()
  const dispatch = useAppDispatch()
  const tableTheme = createTheme({
    typography: {
      fontFamily: '"Open Sans", sans-serif',
      fontSize: 13
    }
  })

  const handleInvitationEmail = async (teamMember: TeamMember) => {
    try {
      await sendValidationEmail(teamMember)
      dispatch(addSuccess('Verification email was sent'))
    } catch (error) {
      console.error(error);
      dispatch(addError(error instanceof Error ? error.message : 'Verification email could not be sent'))
    }
  }

  const handleTeamMember = (teamMember: TeamMember) => {
    setOpenModal({ open: true, teamMember })
  }

  const handleAutoSize = () => {
    autoSizeGrid(apiRef, autosizeOptions)
  }

  const columnGroupingModel: GridColumnGroupingModel = [
    {
      groupId: 'notifications',
      description: 'Notifications',
      headerName: 'Notifications',
      headerAlign: 'center',
      renderHeaderGroup: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px', fontSize: '14px', fontWeight: 'bold' }}>
          {params.headerName ?? params.groupId}
          <Tooltip title="Owner/Admins roles will receive additional notifications">
            <InfoIcon fontSize="small" />
          </Tooltip>
        </Box>
      ),
      children: [
        { field: 'email_bio_passed' },
        { field: 'email_waterline_passed' },
      ],
    },
  ];

  return (
    <ThemeProvider theme={tableTheme}>
      <DataGrid
        apiRef={apiRef}
        sx={{
          borderLeft: "none",
          borderRight: "none",
          borderBottom: "none",
          borderRadius: "0",
          [`& .${gridClasses.cell}`]: {
            display: 'flex',
            alignItems: 'flex-start',
            flexDirection: 'column',
            justifyContent: 'center',
            py: 0.5,
          },
          '& .MuiDataGrid-columnHeader--moving': {
            backgroundColor: theme.palette.common.white,
          }
        }}
        getRowId={row => row.key_}
        rows={teamMembers}
        columnGroupingModel={columnGroupingModel}
        getRowHeight={() => 'auto'}
        autosizeOptions={autosizeOptions}
        loading={isLoading}
        sortingOrder={['asc', 'desc']}
        onRowCountChange={handleAutoSize}
        onPaginationModelChange={handleAutoSize}
        columnVisibilityModel={pending ? {} : {
          status: false,
        }}
        columns={[
          {
            width: 60,
            field: "",
            headerName: "Edit",
            sortable: false,
            disableReorder: true,
            renderCell: (params) => (
              <IconButton aria-label="edit-team-member" onClick={() => handleTeamMember(params.row)}>
                <EditIcon fontSize="small" />
              </IconButton>
            ),
            renderHeader: () => (
              <Box sx={{ fontWeight: theme.typography.fontWeightBold }}>
                Edit
              </Box>
            ),
          },
          {
            minWidth: 200,
            flex: 1,
            field: "fullName",
            headerName: "Name",
            valueGetter: (value, row, column) => getFullName(row),
            renderHeader: () => (
              <Box sx={{ fontWeight: theme.typography.fontWeightBold }}>
                Name
              </Box>
            ),
            getSortComparator: (sortDirection) => {
              const modifier = sortDirection === 'desc' ? -1 : 1

              return (a, b) => {
                if (Boolean(a) && Boolean(b)) {
                  return a.localeCompare(b) * modifier
                } else if (Boolean(a) && !Boolean(b)) {
                  return -1
                } else if (!Boolean(a) && Boolean(b)) {
                  return 1
                } else {
                  return 0
                }
              }
            },
          },
          {
            field: "position",
            headerName: "Job Title",
            minWidth: 220,
            flex: 1,
            renderHeader: () => (
              <Box sx={{ fontWeight: theme.typography.fontWeightBold }}>
                Job Title
              </Box>
            ),
            getSortComparator: (sortDirection) => {
              const modifier = sortDirection === 'desc' ? -1 : 1

              return (a, b) => {
                if (Boolean(a) && Boolean(b)) {
                  return a.localeCompare(b) * modifier
                } else if (Boolean(a) && !Boolean(b)) {
                  return -1
                } else if (!Boolean(a) && Boolean(b)) {
                  return 1
                } else {
                  return 0
                }
              }
            },
          },
          {
            minWidth: 280,
            flex: 1,
            field: "email",
            headerName: "Email",
            renderHeader: () => (
              <Box sx={{ fontWeight: theme.typography.fontWeightBold }}>
                Email
              </Box>
            ),
            getSortComparator: (sortDirection) => {
              const modifier = sortDirection === 'desc' ? -1 : 1

              return (a, b) => {
                if (Boolean(a) && Boolean(b)) {
                  return a.localeCompare(b) * modifier
                } else if (Boolean(a) && !Boolean(b)) {
                  return -1
                } else if (!Boolean(a) && Boolean(b)) {
                  return 1
                } else {
                  return 0
                }
              }
            },
          },
          {
            minWidth: 150,
            flex: 1,
            field: "role",
            headerName: "OnTraq Role",
            valueGetter: (value, row, column) => getRoleName(row.access_level as TeamRoles),
            renderHeader: () => (
              <Box sx={{ fontWeight: theme.typography.fontWeightBold }}>
                OnTraq Role
              </Box>
            ),
          },
          {
            minWidth: 150,
            flex: 1,
            field: "groups",
            headerName: "Groups",
            sortable: false,
            renderCell: params => {
              return getGroups(params.row).map((group, index, groups) => {
                const marginTop = index === 0 && groups.length > 1 ? '8px' : '0px'
                const marginBottom = groups.length > 1
                  ? index === groups.length - 1 ? '8px' : '4px'
                  : '0px'
                const styles = { marginTop, marginBottom, height: '24px' }
                const key = `${params.row.user_id}-${group.group_id}`

                return (
                  <Chip key={key} label={group.name} sx={styles} />
                )
              })
            },
            renderHeader: () => (
              <Box sx={{ fontWeight: theme.typography.fontWeightBold }}>
                Groups
              </Box>
            ),
          },
          {
            minWidth: 120,
            flex: 1,
            field: "email_bio_passed",
            headerName: "Biological",
            valueGetter: (value, row, column) => row.email_bio_passed || row.email_bio_failed ? 1 : 0,
            getSortComparator: (sortDirection) => {
              const modifier = sortDirection === 'desc' ? -1 : 1

              return (a, b) => {
                return (a - b) * modifier;
              };
            },
            renderCell: params => {
              if (!params.value) {
                return <Box></Box>
              }

              return (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%', height: '100%' }}>
                  <Checkbox disabled checked sx={{
                    '&.Mui-checked': {
                      cursor: 'default',
                    },
                  }} />
                </Box>
              )
            },
            renderHeader: () => (
              <Box sx={{ fontWeight: theme.typography.fontWeightBold }}>
                Biological
              </Box>
            ),
            headerAlign: 'center',
          },
          {
            minWidth: 120,
            flex: 1,
            field: "email_waterline_passed",
            headerName: "Waterline",
            valueGetter: (value, row, column) => row.email_waterline_passed || row.email_waterline_failed ? 1 : 0,
            getSortComparator: (sortDirection) => {
              const modifier = sortDirection === 'desc' ? -1 : 1

              return (a, b) => {
                return (a - b) * modifier;
              };
            },
            renderCell: params => {
              if (!params.value) {
                return <Box></Box>
              }

              return (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%', height: '100%' }}>
                  <Checkbox disabled checked sx={{
                    '&.Mui-checked': {
                      cursor: 'default',
                    },
                  }} />
                </Box>
              )
            },
            renderHeader: () => (
              <Box sx={{ fontWeight: theme.typography.fontWeightBold }}>
                Waterline
              </Box>
            ),
            headerAlign: 'center',
          },
          {
            minWidth: 160,
            flex: 1,
            field: "phone",
            headerName: "Phone Number",
            valueGetter: (value, row, column) => getPhoneNumber(row),
            renderHeader: () => (
              <Box sx={{ fontWeight: theme.typography.fontWeightBold }}>
                Phone Number
              </Box>
            ),
            getSortComparator: (sortDirection) => {
              const modifier = sortDirection === 'desc' ? -1 : 1

              return (a, b) => {
                const phone = a.replace(/\D/g, '')
                const phone2 = b.replace(/\D/g, '')

                if (Boolean(phone) && Boolean(phone2)) {
                  return phone.localeCompare(phone2) * modifier
                } else if (Boolean(phone) && !Boolean(phone2)) {
                  return -1
                } else if (!Boolean(phone) && Boolean(phone2)) {
                  return 1
                } else {
                  return 0
                }
              }
            },
          },
          {
            minWidth: 150,
            flex: 1,
            field: "last_connection_date",
            headerName: "Last Login",
            valueGetter: (value, row, column) => getLastLogin(row),
            renderHeader: () => (
              <Box sx={{ fontWeight: theme.typography.fontWeightBold }}>
                Last Login
              </Box>
            ),
            getSortComparator: (sortDirection) => {
              const modifier = sortDirection === 'desc' ? -1 : 1

              return (a, b) => {
                const date = Date.parse(a)
                const date2 = Date.parse(b)

                if (isNaN(date) && isNaN(date2)) {
                  return 0
                } else if (isNaN(date) && !isNaN(date2)) {
                  return 1
                } else if (!isNaN(date) && isNaN(date2)) {
                  return -1
                } else {
                  return (date - date2) * modifier
                }
              }
            },
          },
          {
            minWidth: 220,
            flex: 1,
            field: "status",
            headerName: "Resend Invitation Email",
            renderHeader: () => {
              return (
                <Box sx={{ fontWeight: theme.typography.fontWeightBold }}>
                  Resend Invitation Email
                </Box>
              )
            },
            renderCell: (params) => {
              const status = params.row.verified ? '' : 'Resend Invitation Email';

              return <Box onClick={() => handleInvitationEmail(params.row)} className='team-members-link'>{status}</Box>
            },
          },
        ]}
        initialState={{
          pagination: {
            paginationModel: {
              pageSize: 10,
            },
          },
          sorting: {
            sortModel: [
              {
                field: 'fullName',
                sort: 'asc'
              }
            ]
          }
        }}
        pageSizeOptions={[5, 10, 20, 25, 50, 100]}
        autoHeight
        density="compact"
      />
    </ThemeProvider>
  )
}

function arePropsEqual(prevProps: Readonly<TeamMembersTable>, newProps: Readonly<TeamMembersTable>) {
  const areEqual = prevProps.isLoading === newProps.isLoading
    && prevProps.teamMembers?.length === newProps.teamMembers?.length
    && JSON.stringify(prevProps.teamMembers) === JSON.stringify(newProps.teamMembers)

  return areEqual
}

export default memo(TeamMembersTable, arePropsEqual)
